import { useEffect, useId, useRef, useState } from "react";
import { AivaSparkleIcon } from "../../shared/components/icons";
import { AI_NOTICE_COPY } from "../content";
import { DEFAULT_AI_NOTICE_POLICY, type AiNoticePolicy } from "../policy";
import { formatNoticeDateTime } from "../format";
import type { AiNoticeAcknowledgement } from "../storage";
import styles from "./AiNoticeSheet.module.css";

/** AIVA-8663: the notice in force (Studio text, validity, version); defaults to the built-in notice. */
interface NoticeProps {
  readonly policy?: AiNoticePolicy;
}

interface GateProps extends NoticeProps {
  readonly mode: "gate";
  /** Returns false when the acknowledgement could not be saved. */
  readonly onAcknowledge: () => boolean;
}

interface ReviewProps extends NoticeProps {
  readonly mode: "review";
  readonly acknowledgement: AiNoticeAcknowledgement | null;
  readonly onClose: () => void;
}

export type AiNoticeSheetProps = GateProps | ReviewProps;

/**
 * AIVA-6639 — the AI disclaimer bottom sheet, shared by Word, Outlook and
 * PowerPoint.
 *
 * - `gate`: first run / expired / new notice version. Not dismissible; Continue
 *   stays disabled until the checkbox is ticked.
 * - `review`: re-opened from the footer or More tab. Lighter scrim, no
 *   checkbox, closes on Escape or either close control.
 *
 * The caller marks the rest of the pane `inert` while this is open, which is
 * what keeps focus and clicks inside the sheet.
 */
export function AiNoticeSheet(props: AiNoticeSheetProps) {
  const titleId = useId();
  const bodyId = useId();
  const initialFocusRef = useRef<HTMLInputElement | HTMLButtonElement | null>(null);
  const [checked, setChecked] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const policy = props.policy ?? DEFAULT_AI_NOTICE_POLICY;
  const isReview = props.mode === "review";
  const onClose = isReview ? props.onClose : null;

  // Move focus into the sheet, and hand it back to whatever opened it (the
  // footer link or More-tab card) on close. TaskPane lifts `inert` in a layout
  // effect, which runs before this passive cleanup, so the target is focusable.
  useEffect(() => {
    const opener = document.activeElement;
    initialFocusRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    if (!onClose) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleContinue = () => {
    if (props.mode !== "gate" || !checked) return;
    setSaveFailed(!props.onAcknowledge());
  };

  return (
    <div className={`${styles.scrim} ${isReview ? styles.scrimSoft : ""}`} data-testid="ai-notice-scrim">
      <dialog open className={styles.sheet} aria-modal="true" aria-labelledby={titleId} aria-describedby={bodyId}>
        <div className={styles.headerRow}>
          <span className={styles.mark} aria-hidden="true">
            <AivaSparkleIcon />
          </span>
          <div className={styles.headerText}>
            <h2 id={titleId} className={styles.title}>
              {AI_NOTICE_COPY.title}
            </h2>
            {!isReview && <div className={styles.subtitle}>{AI_NOTICE_COPY.subtitle}</div>}
          </div>
          {isReview && (
            <button
              type="button"
              className={styles.iconClose}
              aria-label={AI_NOTICE_COPY.closeAriaLabel}
              onClick={props.onClose}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          )}
        </div>

        <p id={bodyId} className={styles.body}>
          {policy.text}
        </p>

        {isReview ? (
          <>
            {props.acknowledgement && (
              <div className={styles.ackBox}>
                <svg className={styles.ackTick} viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M4.5 10.3l3.4 3.4 7.6-7.6" />
                </svg>
                <div>
                  <div className={styles.ackLine}>
                    {AI_NOTICE_COPY.acknowledgedLine(formatNoticeDateTime(props.acknowledgement.acknowledgedAt))}
                  </div>
                  <div className={styles.meta}>
                    {AI_NOTICE_COPY.validUntilLine(formatNoticeDateTime(props.acknowledgement.expiresAt), policy.version)}
                  </div>
                </div>
              </div>
            )}
            <button
              type="button"
              ref={(el) => {
                initialFocusRef.current = el;
              }}
              className={`${styles.cta} ${styles.ctaGhost}`}
              onClick={props.onClose}
            >
              {AI_NOTICE_COPY.closeLabel}
            </button>
          </>
        ) : (
          <>
            <div className={styles.rule} />
            <label className={styles.ackRow}>
              <input
                type="checkbox"
                ref={(el) => {
                  initialFocusRef.current = el;
                }}
                checked={checked}
                onChange={(event) => {
                  setChecked(event.target.checked);
                  setSaveFailed(false);
                }}
              />
              <span>{AI_NOTICE_COPY.checkboxLabel}</span>
            </label>
            {saveFailed && (
              <div role="alert" className={styles.error}>
                {AI_NOTICE_COPY.saveError}
              </div>
            )}
            <button type="button" className={styles.cta} disabled={!checked} onClick={handleContinue}>
              {AI_NOTICE_COPY.continueLabel}
            </button>
            <div className={styles.meta}>{AI_NOTICE_COPY.gateMeta(policy.validityDays, policy.version)}</div>
          </>
        )}
      </dialog>
    </div>
  );
}
